import { useEffect, useState } from 'react';
import { Box, Button, IconButton, Paper, Typography } from '@mui/material';
import { holidayConfig, HolidayMode } from '@theme/palettes/getPalette';
import { useApiEnvStore } from '@utils/api-client';
import { celebrate, OPEN_HOLIDAY_PREVIEW_EVENT } from './celebrate';

// what a bind does on each holiday that reacts to it
const BIND_MOMENT_LABELS: Partial<Record<HolidayMode, string>> = {
  [HolidayMode.Christmas]: 'Fly the sleigh',
  [HolidayMode.NewYear]: 'Fire the confetti',
  [HolidayMode.StPatricks]: 'Pour the gold',
};

/**
 * Testing-only picker for forcing a holiday on outside its dates. Hidden until `lio.previewHoliday()` is run in the
 * console, so it never shows up for normal users.
 */
export const HolidayPreviewMenu = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [earlyHolidayMode, disableHolidayModeFlag] = useApiEnvStore((state) => [
    state.earlyHolidayMode,
    state.disableHolidayModeFlag,
  ]);
  const current = disableHolidayModeFlag ? null : earlyHolidayMode;
  const bindMomentLabel = current ? BIND_MOMENT_LABELS[current] : undefined;

  useEffect(() => {
    const open = () => setIsOpen(true);
    window.addEventListener(OPEN_HOLIDAY_PREVIEW_EVENT, open);
    return () => window.removeEventListener(OPEN_HOLIDAY_PREVIEW_EVENT, open);
  }, []);

  if (!isOpen) return null;

  return (
    <Paper elevation={8} sx={sxStyles.panel}>
      <Box sx={sxStyles.titleRow}>
        <Typography variant='body' fontWeight='bold' color='inherit'>
          Preview a holiday
        </Typography>
        <IconButton
          size='small'
          aria-label='Close holiday preview'
          onClick={() => setIsOpen(false)}
          sx={{ color: 'inherit' }}
        >
          ✕
        </IconButton>
      </Box>
      <Box sx={sxStyles.list}>
        {Object.values(HolidayMode).map((holiday) => (
          <Button
            key={holiday}
            size='small'
            variant={current === holiday ? 'contained' : 'outlined'}
            sx={current === holiday ? sxStyles.selected : sxStyles.option}
            onClick={() => window.lio.previewHoliday(holiday)}
          >
            {holidayConfig[holiday].title}
          </Button>
        ))}
      </Box>
      <Box sx={sxStyles.actions}>
        {bindMomentLabel ? (
          <Button size='small' sx={sxStyles.textAction} onClick={() => celebrate('bind')}>
            {bindMomentLabel} (bind)
          </Button>
        ) : (
          <span />
        )}
        <Button
          size='small'
          sx={[sxStyles.textAction, { color: ERROR }]}
          onClick={() => window.lio.previewHoliday('off')}
        >
          Turn off
        </Button>
      </Box>
    </Paper>
  );
};

// fixed colors on purpose: this mounts above the app's page themes, where text colors assume a dark background
const INK = '#1d2b39';
const PAPER = '#ffffff';
const ERROR = '#c62828';

const sxStyles = {
  panel: {
    backgroundColor: PAPER,
    color: INK,
    position: 'fixed',
    left: 16,
    bottom: 16,
    width: 280,
    padding: 2,
    zIndex: 1400,
    display: 'flex',
    flexDirection: 'column',
    gap: 1.5,
  },
  titleRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  list: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: 1,
  },
  actions: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  option: {
    color: INK,
    borderColor: INK,
    textTransform: 'none',
    lineHeight: 1.2,
    '&:hover': { borderColor: INK, backgroundColor: 'rgba(29, 43, 57, 0.06)' },
  },
  selected: {
    color: PAPER,
    backgroundColor: INK,
    textTransform: 'none',
    lineHeight: 1.2,
    '&:hover': { backgroundColor: INK },
  },
  textAction: {
    color: INK,
    textTransform: 'none',
  },
};
